import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  CircularProgress,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Assessment as ReportIcon,
  Email as EmailIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { fileService, CalculatedFile } from '../services/fileService';
import { apiClient } from '../services/api/authApi';
import { AccountType } from '../types/auth';
import PageHeader from '../components/common/PageHeader';
import { getUserTypeLabel } from './_shared/fileUtils';
import LegacyFilesPanel from './_shared/LegacyFilesPanel';
import {
  SortableTableHeaderCell,
  FilesTableContainer,
  FilesTablePagination,
  FilesEmptyState,
  FilesSearchFilterCard,
  DeleteConfirmDialog,
  useEmailDialogState,
  makeHandleSendEmail,
  ConnectedEmailNotificationDialog,
} from './_shared/FilesTableShared';

// Extended interface for local use with additional UI properties
interface ExtendedCalculatedFile extends CalculatedFile {
  resultFileName: string;
  resultFileSize: number;
  calculationResults: {
    totalEmissions: number; // kg CO2
    totalDistance: number; // km
    totalWeight: number; // kg
    shipmentsProcessed: number;
    averageEmissionPerKm: number;
    averageEmissionPerKg: number;
    errorCount?: number;
  };
  status: 'completed' | 'error' | 'expired';
}

// Aggregates come from stored JSON and can be non-numeric (e.g. a string from a
// bad calculation run); coerce so formatting and sums never throw (C4-668).
const toFiniteNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

const CalculatedFilesPage: React.FC = () => {
  const { user } = useAuthStore();
  const [files, setFiles] = useState<ExtendedCalculatedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<ExtendedCalculatedFile | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'fileName' | 'calculatedAt'>('calculatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterCompanyId, setFilterCompanyId] = useState<number | ''>('');
  const [filterCompanyName, setFilterCompanyName] = useState<string>('');
  const [systemPage, setSystemPage] = useState(0);
  const [systemRowsPerPage, setSystemRowsPerPage] = useState(10);
  const [systemTotal, setSystemTotal] = useState(0);
  const [systemSearch, setSystemSearch] = useState('');
  const [view, setView] = useState<'calculated' | 'legacy'>('calculated');
  const [legacyTotal, setLegacyTotal] = useState(0);

  const emailState = useEmailDialogState();

  const isMasterAdmin = user?.accountType === AccountType.MasterAdmin || user?.accountType === AccountType.CarbonCareAdmin;

  const handleSort = (column: string) => {
    const col = column as 'fileName' | 'calculatedAt';
    if (sortBy === col) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDirection(col === 'fileName' ? 'asc' : 'desc');
    }
    setSystemPage(0);
  };

  const fetchSystemFiles = async (pg = systemPage, lim = systemRowsPerPage, search = systemSearch, currentSortBy = sortBy, currentSortDirection = sortDirection) => {
    try {
      setLoading(true);
      const result = await fileService.getCalculatedFiles({ page: pg + 1, limit: lim, search: search || undefined, companyName: filterCompanyName || undefined, sortBy: currentSortBy, sortDirection: currentSortDirection });
      const calculatedFiles = result.data;
      setSystemTotal(result.pagination.total);

        // Transform API data to match local interface
        const transformedFiles: ExtendedCalculatedFile[] = calculatedFiles.map(file => ({
          // FileInfo properties
          id: file.id,
          fileName: file.fileName || file.originalName,
          originalName: file.originalName,
          fileSize: file.fileSize || 0,
          uploadedAt: file.uploadedAt || new Date(),
          uploadedBy: file.uploadedBy || { id: 0, name: 'Unknown', email: '' },
          companyId: file.companyId,
          companyName: file.companyName,
          userType: file.userType,

          // ProcessedFile properties
          processedAt: file.processedAt || new Date(),
          processedBy: file.processedBy || file.calculatedBy,
          schema: file.settings?.schemaName || file.schema || 'Default Schema',
          settings: file.settings || {
            hasHeader: true,
            useStreetDistances: true,
            standard: 'ISO',
          },
          progress: file.progress || 100,

          // CalculatedFile properties
          calculatedAt: file.calculatedAt || new Date(),
          calculatedBy: file.calculatedBy || file.uploadedBy || { id: 0, name: 'System', email: '' },

          // ExtendedCalculatedFile properties
          resultFileName: file.originalName.replace(/\.(xlsx|csv)$/, '_results.xlsx'),
          resultFileSize: Math.floor((file.fileSize || 0) * 1.2), // Assume results are 20% larger
          calculationResults: {
            totalEmissions: toFiniteNumber(file.results?.totalTOT),
            totalDistance: toFiniteNumber(file.results?.totalDistance),
            totalWeight: toFiniteNumber(file.results?.totalWeight),
            shipmentsProcessed: toFiniteNumber(file.results?.totalShipments),
            averageEmissionPerKm: toFiniteNumber(file.results?.totalDistance) > 0 ? toFiniteNumber(file.results?.totalTOT) / toFiniteNumber(file.results?.totalDistance) : 0,
            averageEmissionPerKg: toFiniteNumber(file.results?.averageCO2),
            errorCount: toFiniteNumber(file.results?.errorCount),
          },
          status: file.status === 'calculated' ? 'completed' as const : 'error' as const,
        }));

        setFiles(transformedFiles);
      } catch (error) {
        console.error('Error fetching calculated files:', error);
        setError('Failed to load calculated files');
      } finally {
        setLoading(false);
      }
  };

  const prevSystemSearch = React.useRef(systemSearch);
  const prevSystemCompany = React.useRef(filterCompanyName);

  useEffect(() => {
    if (view === 'legacy') return;

    const searchChanged = prevSystemSearch.current !== systemSearch;
    const companyChanged = prevSystemCompany.current !== filterCompanyName;
    prevSystemSearch.current = systemSearch;
    prevSystemCompany.current = filterCompanyName;

    if (searchChanged) {
      const timer = setTimeout(() => {
        setSystemPage(0);
        fetchSystemFiles(0, systemRowsPerPage, systemSearch);
      }, 600);
      return () => clearTimeout(timer);
    }

    if (companyChanged) {
      setSystemPage(0);
      fetchSystemFiles(0, systemRowsPerPage, systemSearch);
      return;
    }

    fetchSystemFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, systemPage, systemRowsPerPage, systemSearch, filterCompanyName, sortBy, sortDirection, view]);

  const formatEmissions = (value: unknown) => {
    const kg = toFiniteNumber(value);
    if (kg >= 1000) {
      return (kg / 1000).toFixed(2) + ' T CO₂';
    }
    return kg.toFixed(2) + ' kg CO₂';
  };

  const formatDistance = (value: unknown) => {
    return new Intl.NumberFormat().format(Math.round(toFiniteNumber(value))) + ' km';
  };

  const formatWeight = (value: unknown) => {
    const kg = toFiniteNumber(value);
    if (kg >= 1000) {
      return (kg / 1000).toFixed(1) + ' T';
    }
    return Math.round(kg) + ' kg';
  };

  const handleDownload = async (file: ExtendedCalculatedFile) => {
    try {
      await fileService.downloadFile(file.id);
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file');
    }
  };

  const handleDelete = async (file: ExtendedCalculatedFile) => {
    try {
      await fileService.deleteFile(file.id);
      setDeleteDialogOpen(false);
      setSelectedFile(null);
      fetchSystemFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      setError('Failed to delete file');
    }
  };

  const handleSendEmail = makeHandleSendEmail({
    fileId: selectedFile?.id,
    customEmails: emailState.customEmails,
    emailComment: emailState.emailComment,
    selectedUserIds: emailState.selectedUserIds,
    setSendingEmail: emailState.setSendingEmail,
    resetEmailDialog: emailState.resetEmailDialog,
    setError,
  });


  return (
    <Box>
      <PageHeader
        description={
          view === 'legacy'
            ? 'Archived files migrated from the previous CarbonCare system. Download only.'
            : 'Files with completed emissions calculations ready to review.'
        }
      />

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 99999 }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Filters */}
      <FilesSearchFilterCard
        searchValue={systemSearch}
        onSearchChange={setSystemSearch}
        isMasterAdmin={isMasterAdmin}
        filterCompanyId={filterCompanyId}
        onFilterCompanyChange={(id, name) => { setFilterCompanyId(id); setFilterCompanyName(name); }}
        total={view === 'legacy' ? legacyTotal : systemTotal}
        extraItems={isMasterAdmin ? (
          <Grid item xs={12} sm="auto">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, next) => { if (next) setView(next); }}
              aria-label="File set"
            >
              <ToggleButton value="calculated">Current</ToggleButton>
              <ToggleButton value="legacy">Legacy Files</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        ) : undefined}
      />

      {/* Summary Cards */}
      {view === 'calculated' && user?.accountType! >= AccountType.CarbonCareAdmin && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="h6" color="success.main">
                  {files.filter(f => f.status === 'completed').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed Calculations
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="h6" color="primary.main">
                  {formatEmissions(files.reduce((sum, f) => sum + f.calculationResults.totalEmissions, 0))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Emissions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="h6" color="info.main">
                  {files.reduce((sum, f) => sum + f.calculationResults.shipmentsProcessed, 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Shipments Processed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Files Table */}
      {view === 'legacy' ? (
        <LegacyFilesPanel
          search={systemSearch}
          companyId={filterCompanyId}
          onTotalChange={setLegacyTotal}
          onError={setError}
        />
      ) : (
        <>
          <FilesTableContainer loading={loading}>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableHeaderCell
                    columnKey="fileName"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    defaultDirection="asc"
                  >
                    File Name
                  </SortableTableHeaderCell>
                  <TableCell>Results</TableCell>
                  {user?.accountType && user?.accountType >= AccountType.CarbonCareAdmin && <TableCell>Licensee</TableCell>}
                  <TableCell>Calculated By</TableCell>
                  <SortableTableHeaderCell
                    columnKey="calculatedAt"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    defaultDirection="desc"
                  >
                    Calculation Date
                  </SortableTableHeaderCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {file.originalName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {file.schema}
                          </Typography>
                        </Box>
                        {user?.accountType! >= AccountType.CarbonCareAdmin && file?.calculationResults?.errorCount && file?.calculationResults?.errorCount > 0 ? (
                          <Tooltip title={`${file.calculationResults.errorCount} calculation errors found`}>
                            <ErrorIcon color="warning" fontSize="small" />
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {file.status === 'completed' ? (
                        <Box>
                          <Typography variant="body2" color="success.main">
                            {formatEmissions(file.calculationResults.totalEmissions)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {file.calculationResults.shipmentsProcessed} shipments •
                            {formatDistance(file.calculationResults.totalDistance)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {file.status === 'error' ? 'Calculation failed' : 'No results'}
                        </Typography>
                      )}
                    </TableCell>
                    {user?.accountType && user?.accountType >= AccountType.CarbonCareAdmin && (
                      <TableCell>{file.companyName}</TableCell>
                    )}
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {file.calculatedBy?.name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getUserTypeLabel(file.userType)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {file.calculatedAt ?
                        `${new Date(file.calculatedAt).toLocaleDateString()} ${new Date(file.calculatedAt).toLocaleTimeString()}` :
                        'N/A'
                      }
                    </TableCell>
                    <TableCell align="right">
                      {file.status === 'completed' && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(file)}
                            title="Download"
                          >
                            <DownloadIcon />
                          </IconButton>
                          <Tooltip title="View Report">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedFile(file);
                                setReportDialogOpen(true);
                              }}
                              color="info"
                            >
                              <ReportIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send Email">
                            <IconButton
                              size="small"
                              onClick={async () => {
                                emailState.setEmailLoadingFileId(file.id);
                                setSelectedFile(file);
                                try {
                                  await apiClient.get(`/companies/${file.companyId}`);
                                  const usersResponse = await apiClient.get(`/companies/${file.companyId}/users`);
                                  if (usersResponse.data.success) {
                                    emailState.setCompanyUsers(usersResponse.data.data || []);
                                  }
                                } catch (error) {
                                  console.error('Error fetching company data:', error);
                                  emailState.setCompanyUsers([]);
                                } finally {
                                  emailState.setEmailLoadingFileId(null);
                                }
                                emailState.setEmailDialogOpen(true);
                              }}
                              color="secondary"
                              disabled={emailState.emailLoadingFileId === file.id}
                            >
                              {emailState.emailLoadingFileId === file.id ? <CircularProgress size={18} /> : <EmailIcon />}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedFile(file);
                            setDeleteDialogOpen(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </FilesTableContainer>
          <FilesTablePagination
            total={systemTotal}
            page={systemPage}
            rowsPerPage={systemRowsPerPage}
            onPageChange={setSystemPage}
            onRowsPerPageChange={(n) => { setSystemRowsPerPage(n); setSystemPage(0); }}
          />

          <FilesEmptyState
            loading={loading}
            filesLength={files.length}
            primaryText="No calculated files found"
            secondaryText="No files have been calculated yet."
          />
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => selectedFile && handleDelete(selectedFile)}
        title="Delete Calculated File"
        message={`Are you sure you want to delete the calculation results for "${selectedFile?.originalName}"? This action cannot be undone.`}
      />

      {/* Report Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Calculation Report</DialogTitle>
        <DialogContent>
          {selectedFile && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {selectedFile.originalName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Calculated using {selectedFile.schema} • {selectedFile.settings.standard} Standard
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="h6" color="success.main">
                      {formatEmissions(selectedFile?.calculationResults?.totalEmissions)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total CO₂ Emissions
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="h6" color="primary.main">
                      {selectedFile?.calculationResults?.shipmentsProcessed}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Shipments Processed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="h6" color="info.main">
                      {formatDistance(selectedFile?.calculationResults?.totalDistance)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Distance
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="h6" color="warning.main">
                      {formatWeight(selectedFile?.calculationResults?.totalWeight)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Weight
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Efficiency Metrics
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2">
                    <strong>{selectedFile?.calculationResults?.averageEmissionPerKm?.toFixed(3)} kg CO₂/km</strong>
                  </Typography>
                  <Typography variant="body2">
                    <strong>{selectedFile?.calculationResults?.averageEmissionPerKg?.toFixed(3)} kg CO₂/kg</strong>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} variant="outlined">Close</Button>
          {selectedFile && (
            <Button
              onClick={() => handleDownload(selectedFile)}
              variant="contained"
              startIcon={<DownloadIcon />}
            >
              Download Results
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConnectedEmailNotificationDialog
        emailDialogState={emailState}
        fileName={selectedFile?.originalName}
        onSend={handleSendEmail}
      />
    </Box>
  );
};

export default CalculatedFilesPage;
